import type { TrainingDiagram, TrainingExerciseDto, TrainingPlanDto } from '@ukinfo/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../../lib/api';
import { fetchExercises } from '../../lib/offline';
import {
  buildSessionImageData,
  getImageBrand,
  makeSessionImage,
  makeSketchImage,
  safeFileName,
  shareOrDownload,
} from '../../lib/share-image';
import { Button, useOptionalToast } from '../ui';
import { sketchPicture } from './sketchSvg';

/**
 * "Als Bild": Skizze oder ganze Einheit als PNG teilen (Handy: Teilen-Fenster, sonst Download).
 * Alles geschieht im Browser, auch ohne Netz.
 */

function ImageIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <circle cx="9" cy="10" r="1.6" />
      <path d="M21 16l-5-5-8 8" />
    </svg>
  );
}

interface Built {
  blob: Blob;
  fileName: string;
  title: string;
}

function ShareImageButton({ label, ariaLabel, titleHint, build, className }: { label: string; ariaLabel: string; titleHint: string; build: () => Promise<Built>; className?: string }): JSX.Element {
  const toast = useOptionalToast();
  const [busy, setBusy] = useState(false);

  const run = async (): Promise<void> => {
    setBusy(true);
    try {
      const { blob, fileName, title } = await build();
      const outcome = await shareOrDownload(blob, fileName, title);
      if (outcome === 'downloaded') toast?.show(`Bild gespeichert: ${fileName}`);
    } catch (error) {
      toast?.show(error instanceof Error ? error.message : 'Das Bild ließ sich nicht erzeugen', 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button type="button" variant="secondary" size="sm" className={className} aria-label={ariaLabel} title={titleHint} loading={busy} onClick={() => void run()}>
      {!busy && <ImageIcon />}
      {label}
    </Button>
  );
}

/** Knopf an einer Skizze: nur die Skizze, mit Titel der Übung darunter. */
export function SketchImageButton({ diagram, title, className }: { diagram: TrainingDiagram; title: string; className?: string }): JSX.Element {
  return (
    <ShareImageButton
      label="Als Bild"
      ariaLabel="Skizze als Bild"
      titleHint="Skizze als Bild teilen oder speichern"
      className={className}
      build={async () => ({
        blob: await makeSketchImage(sketchPicture(diagram, title), title, getImageBrand()),
        fileName: safeFileName(title || 'skizze'),
        title: title || 'Skizze',
      })}
    />
  );
}

/**
 * Knopf an einer Einheit: Titel, Termin, je Phase die Übungen mit Minuten, Skizze und Coaching-Punkten.
 * Die Beschreibungen kommen aus `exercises` (gespeicherte Einheit) oder, sonst, aus dem Zwischenspeicher
 * der Abfragen bzw. vom Server; fehlt eine, entfallen nur ihre Coaching-Punkte.
 */
export function PlanImageButton({
  plan,
  subtitle,
  exercises,
  className,
}: {
  plan: TrainingPlanDto;
  subtitle: string;
  exercises?: Record<string, TrainingExerciseDto>;
  className?: string;
}): JSX.Element {
  const queryClient = useQueryClient();
  return (
    <ShareImageButton
      label="Einheit als Bild"
      ariaLabel="Einheit als Bild"
      titleHint="Die ganze Einheit als Bild teilen oder speichern"
      className={className}
      build={async () => {
        let details = exercises;
        if (!details) {
          try {
            details = await fetchExercises(plan, (id) =>
              queryClient.fetchQuery({ queryKey: ['training-exercise', id], queryFn: () => api.get<TrainingExerciseDto>(`/api/training/exercises/${id}`), staleTime: 60_000 }));
          } catch {
            details = {};
          }
        }
        const data = buildSessionImageData(plan, details, { subtitle, brand: getImageBrand(), renderSketch: sketchPicture });
        return { blob: await makeSessionImage(data), fileName: safeFileName(plan.title), title: plan.title };
      }}
    />
  );
}
